import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import {
  Divider,
  FileDragDropIcon,
  Grid,
  GridItem,
  Tab,
  TabBox,
  TabPanel,
  Tabs,
  Typography,
  useAlert,
} from 'fasoo-ui-component-next';
import _ from 'lodash';

import approvalDetailApi, {
  ApprovalDetailEntity,
  ApprovalStatusType,
  RequestApprovalDto,
} from 'src/api/user/approvalApi';
import { ProvisionalApprovalDetailEtity } from 'src/api/user/provisionalApprovalApi';
import { CommonProps } from 'src/components/helpers/props';
import usePopup from 'src/hooks/usePopup';
import { getFasooUiComponentLanguage } from 'src/i18n';
import { toLocalDateTimeLangStr } from 'src/shared/utils/localDateTime';
import { getIsManagerDownloadRemark } from 'src/shared/utils/sessionStorageManager';

import { ApprovalDetailFooter } from '../ApprovalDetailFooter';

import { ApprovalTypeIconArea } from './ApprovalTypeIconArea';
import RemarkPopup from './RemarkPopup';
import {
  ApprovalDetailInfoPageType,
  ApprovalUserInfoDto,
  DownloadHistoryInfo,
  FileIcon,
  ShowLineContent,
  UserDetail,
} from '.';

import './ProvisionalDetail.scss';

interface ProvisionalApprovalDetailProps extends CommonProps {
  provisionalApprovalDetail: ProvisionalApprovalDetailEtity;
  ResetDetail: () => void;
  cancelApprovalChildren?: React.ReactNode;
  onCloseDetail: (open: boolean, isReject?: boolean) => void;
  isComplete?: boolean;
  selectedApproval?: ApprovalDetailEntity | null;
  pageType: ApprovalDetailInfoPageType;
}

const ProvisionalDetail: React.FC<ProvisionalApprovalDetailProps> = ({
  provisionalApprovalDetail,
  ResetDetail,
  cancelApprovalChildren,
  onCloseDetail,
  isComplete = false,
  selectedApproval,
  pageType,
}) => {
  const { t } = useTranslation(['menu', 'userApproval', 'fileType']);
  const { alert } = useAlert();
  const [tab, setTab] = useState(0);

  const isWriteDownloadReason = pageType === 'managerFile' && getIsManagerDownloadRemark();
  // 사용 이력의 사유 보기, 관리자(일반 관리자 + 감사자)일 경우 + 사유 입력 옵션 true일 경우 사용
  const isManagerDownloadReason =
    (pageType === 'managerFile' || pageType === 'managerLog') && getIsManagerDownloadRemark();
  const openApprovalPopup = usePopup();
  const openRejectPopup = usePopup();

  let approvers: ApprovalUserInfoDto[] = [];
  if (provisionalApprovalDetail.completeApprovers.length > 0) {
    const completes = _.sortBy(provisionalApprovalDetail.completeApprovers, 'step');
    approvers = approvers.concat(
      completes.map((approver) => {
        return {
          step: approver.step,
          approvalStatus: approver.approvalStatus,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
          actorIp: approver.actorIp,
          approvalRemark: approver.approvalRemark,
          approvalDate: approver.updatedTs,
          method: approver.approvalMethod,
          otherDeptCode: approver.otherDeptCode === null ? null : approver.otherDeptCode,
          otherDeptName: approver.otherDeptName === null ? null : approver.otherDeptName,
          otherPositionCode:
            approver.otherPositionCode === null ? null : approver.otherPositionCode,
          otherPositionName:
            approver.otherPositionName === null ? null : approver.otherPositionName,
          otherRoleCode: approver.otherRoleCode === null ? null : approver.otherRoleCode,
          otherRoleName: approver.otherRoleName === null ? null : approver.otherRoleName,
          otherUserId: approver.otherUserId === null ? null : approver.otherUserId,
          otherUserName: approver.otherUserName === null ? null : approver.otherUserName,
        };
      })
    );
  }
  if (provisionalApprovalDetail.progressApprovers.length > 0) {
    const progresses = _.sortBy(provisionalApprovalDetail.progressApprovers, 'step');
    approvers = approvers.concat(
      progresses.map((approver) => {
        return {
          step: approver.step,
          approvalStatus: ApprovalStatusType.PROGRESS,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
        };
      })
    );
  }
  // 결재선이 다 끝나지 않는 단계
  // if (provisionalApprovalDetail.currentStep < provisionalApprovalDetail.approvalStep) {
  //   let postApprovalSteps: number[] = [];
  //   for (
  //     let i = provisionalApprovalDetail.currentStep + 1;
  //     i <= provisionalApprovalDetail.approvalStep;
  //     i++
  //   ) {
  //     postApprovalSteps = postApprovalSteps.concat(i);
  //   }

  approvers = approvers.concat(
    provisionalApprovalDetail.approvers
      .filter(
        (approver) =>
          // postApprovalSteps.includes(approver.step) &&
          approvers.filter((v) => v.step === approver.step)?.length === 0
      )
      .map((approver) => {
        return {
          step: approver.step,
          approvalStatus: ApprovalStatusType.PROGRESS,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
        };
      })
  );
  // }

  const rightList: string[] = [];
  if (provisionalApprovalDetail.rightView === 'Y') {
    rightList.push('View');
  }
  if (provisionalApprovalDetail.rightEdit === 'Y') {
    rightList.push('Edit');
  }
  if (provisionalApprovalDetail.rightPrintScreen === 'Y') {
    rightList.push('PrintScreen');
  }
  if (provisionalApprovalDetail.rightPrint === 'Y') {
    rightList.push('Print');
  }
  if (provisionalApprovalDetail.rightSecurePrint === 'Y') {
    rightList.push('SecurePrint');
  }
  if (provisionalApprovalDetail.rightSave === 'Y') {
    rightList.push('Save');
  }
  if (provisionalApprovalDetail.rightTransfer === 'Y') {
    rightList.push('Transfer');
  }
  if (provisionalApprovalDetail.rightClassTransfer === 'Y') {
    rightList.push('ClassTransfer');
  }
  if (provisionalApprovalDetail.rightExtract === 'Y') {
    rightList.push('Extract');
  }
  if (provisionalApprovalDetail.rightMacro === 'Y') {
    rightList.push('Macro');
  }
  if (provisionalApprovalDetail.rightConversion === 'Y') {
    rightList.push('Conversion');
  }
  if (provisionalApprovalDetail.rightBranch === 'Y') {
    rightList.push('Branch');
  }
  if (provisionalApprovalDetail.rightUnwrap === 'Y') {
    rightList.push('Unwrap');
  }

  const handleOpenApprovalPopup = (type: string): void => {
    if (type === 'approval') {
      openApprovalPopup.openPopup();
    } else if (type === 'reject') {
      openRejectPopup.openPopup();
    }
  };

  const handleCloseApprovalPopup = (type: string): void => {
    if (type === 'approval') {
      openApprovalPopup.closePopup();
    } else if (type === 'reject') {
      openRejectPopup.closePopup();
    }
  };

  const handleApprovalReject = (remark: string): void => {
    const popupType = openApprovalPopup.opened !== false ? 'approval' : 'reject';

    // console.log('얘가 작성한 remark : ', remark);
    if (provisionalApprovalDetail.progressApprovers.length > 0) {
      const progressApproverInfo = provisionalApprovalDetail.progressApprovers[0];
      const reqApprovalDto: RequestApprovalDto = {
        userId: progressApproverInfo.userId,
        approvalNo: provisionalApprovalDetail.approvalNo,
        approvalMethod: provisionalApprovalDetail.approvalMethod,
        approvalType: provisionalApprovalDetail.approvalType,
        status: popupType === 'approval' ? 'APPROVAL' : 'REJECT',
        remark,
        proxyNo: selectedApproval?.progressApprovers[0].proxyNo
          ? selectedApproval.progressApprovers[0].proxyNo
          : null,
      };
      approvalDetailApi
        .approvalOrReject(reqApprovalDto)
        .then(() => {
          onCloseDetail(false, true);
        })
        .catch((err) => alert(err, 'error', '', undefined, true));
    } else {
      onCloseDetail(false);
    }

    handleCloseApprovalPopup(popupType);
  };

  return (
    <div className="provisional-detail-root">
      <div
        className={classNames('provisional-detail-maincontainer', {
          'mg mb-85': isComplete === false,
          // 'mg mb-32': isComplete === true,
        })}
      >
        <ApprovalTypeIconArea
          onClose={onCloseDetail}
          type={provisionalApprovalDetail.approvalType}
          status={provisionalApprovalDetail.status}
        />
        <TabBox tabWidth="full" line>
          <Tabs
            value={tab}
            onChange={(selected) => {
              setTab(selected);
              if (selected === 1) {
                ResetDetail();
              }
            }}
          >
            <Tab label={t('userApproval:tab.basicInfo')} index={0} />
            <Tab label={t('userApproval:tab.usageLogs')} index={1} />
          </Tabs>
          <TabPanel value={tab} index={0}>
            {!provisionalApprovalDetail.fileDetail ||
              provisionalApprovalDetail.applyType === 'CLASSCODE' || (
                <ShowLineContent title={t('userApproval:fileDetails.$')}>
                  <Grid spacing={0}>
                    <GridItem xs={12} padding="24px 0px 0px 0px">
                      <Grid spacing={0}>
                        <GridItem xs={6} alignSelf="center">
                          <Typography variant="h4">{t('userApproval:fileDetails.$')}</Typography>
                          <span className="file-count">
                            {provisionalApprovalDetail.fileDetail ? 1 : 0}
                          </span>
                        </GridItem>
                      </Grid>
                    </GridItem>
                    <GridItem xs={12} classes={classNames('files-overflow')}>
                      <div className="pt-10 flex">
                        {FileIcon(provisionalApprovalDetail.fileDetail.fileName)}
                        <div className="download-file-name">
                          {provisionalApprovalDetail.fileDetail.fileName}
                        </div>
                      </div>
                    </GridItem>
                  </Grid>
                </ShowLineContent>
              )}
            <ShowLineContent
              title={t('userApproval:requestRange.$')}
              content={t(`userApproval:requestRange.${provisionalApprovalDetail.applyType}`, {
                codeName: provisionalApprovalDetail.fileDetail?.codeName,
                fileTypeStr: provisionalApprovalDetail.fileDetail?.fileType
                  ? t(`fileType:fileType.${provisionalApprovalDetail.fileDetail.fileType}`)
                  : '',
              })}
            />
            {/* 수정 필요 */}
            <ShowLineContent
              title={t('userApproval:approvalRight.$')}
              content={rightList
                .map((right) => t(`userApproval:approvalRight.${right}`))
                .join(' / ')}
            />
            <ShowLineContent
              title={t('userApproval:rightPeriod')}
              content={
                provisionalApprovalDetail.startDate < 1
                  ? t('commons:unlimited')
                  : `${toLocalDateTimeLangStr(
                      provisionalApprovalDetail.startDate,
                      getFasooUiComponentLanguage()
                    )} ~ ${toLocalDateTimeLangStr(
                      provisionalApprovalDetail.endDate,
                      getFasooUiComponentLanguage()
                    )}`
              }
            />
            {!provisionalApprovalDetail.approvalMethod || (
              <ShowLineContent
                classes={classNames('approval-method')}
                title={t('userApproval:approvalMethod.$')}
                content={t(
                  `userApproval:approvalMethod.${provisionalApprovalDetail.approvalMethod}`
                )}
              />
            )}
            <ShowLineContent
              title={t('userApproval:applicant')}
              content={
                <UserDetail
                  type="request"
                  dateStr={toLocalDateTimeLangStr(
                    provisionalApprovalDetail.applyDate,
                    getFasooUiComponentLanguage()
                  )}
                  remark={provisionalApprovalDetail.remark}
                  userInfo={{
                    userId: provisionalApprovalDetail.userId,
                    userName: provisionalApprovalDetail.userName,
                    positionName: provisionalApprovalDetail.positionName,
                    deptName: provisionalApprovalDetail.deptName,
                    actorIp: provisionalApprovalDetail.actorIp,
                  }}
                />
              }
            />
            <div className="approval-detail-info-title">
              <div className="title-text">{t('userApproval:approvalDetail.$')}</div>
            </div>
            <ShowLineContent
              title=""
              content={
                <>
                  {approvers.map((approver, idx) => {
                    return (
                      <UserDetail
                        step={approvers.length > 1 ? approver.step : undefined}
                        type="approver"
                        dateStr={
                          approver.approvalDate
                            ? toLocalDateTimeLangStr(
                                approver.approvalDate,
                                getFasooUiComponentLanguage()
                              )
                            : null
                        }
                        remark={approver.approvalRemark ? approver.approvalRemark : null}
                        userInfo={approver}
                        key={`approver-${idx}`}
                      />
                    );
                  })}

                  {approvers.length <= 1 || <Divider />}
                </>
              }
            />
            {cancelApprovalChildren}
          </TabPanel>
          <TabPanel value={tab} index={1}>
            {provisionalApprovalDetail.downloadHistory.length > 0 ? (
              provisionalApprovalDetail.downloadHistory.map((v) => {
                return (
                  <DownloadHistoryInfo
                    fileName={provisionalApprovalDetail.fileDetail.fileName || ''}
                    updatedTsStr={toLocalDateTimeLangStr(
                      v.updatedTs,
                      getFasooUiComponentLanguage()
                    )}
                    userName={v.userName}
                    userId={v.userId}
                    userIpText={v.userIpText}
                    action={v.action}
                    adminRemark={isManagerDownloadReason ? v.adminRemark || '' : null}
                  />
                );
              })
            ) : (
              <div className={classNames(`download-history-root`)}>
                <div style={{ height: '32px' }} />
                <FileDragDropIcon />
                <div style={{ height: '32px' }} />
                <Typography variant="p" color="secondary">
                  {t('userApproval:message.noFileHistory')}
                </Typography>
              </div>
            )}
          </TabPanel>
        </TabBox>
      </div>
      {isComplete === false && (
        <ApprovalDetailFooter
          approvalMethod={provisionalApprovalDetail.approvalMethod}
          handleApprove={() => handleOpenApprovalPopup('approval')}
          handleReject={() => handleOpenApprovalPopup('reject')}
        />
      )}
      {(openApprovalPopup.opened === true || openRejectPopup.opened === true) && (
        <RemarkPopup
          open={
            openApprovalPopup.opened === true ? openApprovalPopup.opened : openRejectPopup.opened
          }
          entity={provisionalApprovalDetail}
          onSubmit={handleApprovalReject}
          onClose={handleCloseApprovalPopup}
          isReject={openApprovalPopup.opened !== true}
        />
      )}
    </div>
  );
};

export default ProvisionalDetail;
